import { Appearance, loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { getPaymentAmount } from '@app/common/src/components/PlanPurchaseExperience/utils/GetPaymentAmount'
import { theme } from '@dtx-company/design-system/src'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useSWRPlus } from '@dtx-company/inter-app/src/services/swrPlus'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import { variants } from '@dtx-company/design-system/src/components/Typography/Typography.overrides'
import React from 'react'

export interface StripeElementsProps {
  amount?: number
  children: React.ReactNode
}

export const StripeElements: React.FC<StripeElementsProps> = ({ children, amount }) => {
  // Ensure the stripe object doesn't change by only loading it once
  const stripeSWR = useSWRPlus('stripePromise', () => loadStripe(process.env.STRIPE_API_KEY), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  })
  const [disableAutomaticPaymentMethodTypes, values] = useFlowFeature(
    'disable_automatic_payment_method_types'
  )
  const paymentMethods = values?.defaultPaymentMethods || 'card'
  const usePaymentMethods = paymentMethods.split(',')
  const { isMobile } = useScreenSizeBreakpoints()
  return (
    <Elements
      stripe={stripeSWR.data || null}
      options={{
        mode: 'subscription',
        amount: getPaymentAmount(amount),
        appearance: appearance(isMobile),
        currency: 'usd',
        paymentMethodTypes:
          disableAutomaticPaymentMethodTypes && usePaymentMethods ? usePaymentMethods : undefined,
        // we're going to submit payments manually
        paymentMethodCreation: 'manual',
        paymentMethodOptions: {
          us_bank_account: { verification_method: 'instant' }
        }
      }}
    >
      {children}
    </Elements>
  )
}

const appearance = (isMobile: boolean): Appearance => ({
  theme: 'stripe',

  variables: {
    colorPrimary: theme.palette.primary.main,
    colorBackground: theme.palette.background.default,
    colorSuccess: theme.palette.success.main,
    colorText: theme.palette.text.primary,
    colorDanger: theme.palette.error.main,
    fontFamily: variants.caption.fontFamily,
    spacingUnit: isMobile ? '4px' : '8px',
    spacingGridRow: '16px',
    spacingGridColumn: isMobile ? '8px' : '16px',
    borderRadius: '0'
  },

  rules: {
    '.Tab': {
      border: `1px solid ${theme.palette.border.outline}`,
      boxShadow: 'none',
      padding: '1rem',
      margin: '0 0.25rem'
    },
    '.Tab:hover': {
      boxShadow: theme.shadows[2]
    },
    '.Tab--selected': {
      border: 'none',
      outline: `2px solid ${theme.palette.text.primary}`,
      color: theme.palette.info.main
    },
    '.Tab--selected:hover': {
      color: theme.palette.info.main,
      outline: `2px solid ${theme.palette.text.primary}`,
      boxShadow: theme.shadows[2]
    },
    '.TabIcon--selected': {
      fill: theme.palette.info.main
    },
    '.Label': {
      ...variants.labelMedium
    },
    '.Input': {
      ...variants.labelLarge,
      padding: '12px 12px 12px 16px',
      shadow: 'none',
      border: `1px solid ${theme.palette.border.outline}`
    },
    '.PickerItem': {
      border: `1px solid ${theme.palette.border.outline}`,
      boxShadow: 'none'
    },
    '.PickerItem:hover': {
      boxShadow: theme.shadows[2],
      backgroundColor: theme.palette.background.default
    }
  }
})
